/**
 * @fileoverview System report/document template service — thin binding of the shared
 * createReportTemplateService factory to the system Dexie (G-DRY-SVC).
 * @module services/systemReportTemplateService
 *
 * Responsibilities: - Expose the local-first report-template API bound to the SYSTEM Dexie,
 *                     plus system-only library-visibility controls.
 * Dependencies:     - @firesquirrel/local-db (createReportTemplateService, getSystemDb)
 *                   - ./systemAuditLogService (writeAuditLog for mutation audit entries)
 * Architecture:     - Local-first: Dexie-first reads/writes + wakeLocalWrite via the factory.
 *                     No API calls. Replication reconciles MongoDB reportTemplates.
 * Notes:            - System variant uses idPrefix 'sys-rtpl', copy suffix ' (Copy)', and
 *                     defaultActor 'system-admin' to preserve prior behavior. Library/
 *                     visibility helpers are system-only and wrap updateReportTemplate.
 */

import { createReportTemplateService, getSystemDb } from '@firesquirrel/local-db';
import type { ReportTemplateLocal, ListReportTemplatesParams } from '@firesquirrel/local-db';
import { writeAuditLog } from './systemAuditLogService';

const service = createReportTemplateService({
  getDb: getSystemDb,
  sourceLevel: 'system',
  idPrefix: 'sys-rtpl',
  copyNameSuffix: ' (Copy)',
  defaultActor: 'system-admin',
  // The factory's WriteAuditLogFn uses `action: string` (loose, to avoid coupling
  // local-db to @firesquirrel/audit); writeAuditLog uses `action: AuditAction` (narrow
  // union). The factory only ever passes valid AuditAction values, so this cast is
  // safe at runtime. Same seam as web/tenant-web reportTemplateService.
  writeAuditLog: (opts) => writeAuditLog(opts as Parameters<typeof writeAuditLog>[0]),
});

export type { ListReportTemplatesParams };

export const listReportTemplates = service.listReportTemplates;
export const getReportTemplate = service.getReportTemplate;
export const createReportTemplate = service.createReportTemplate;
export const updateReportTemplate = service.updateReportTemplate;
export const deleteReportTemplate = service.deleteReportTemplate;
export const publishReportTemplate = service.publishReportTemplate;
export const archiveReportTemplate = service.archiveReportTemplate;
export const duplicateReportTemplate = service.duplicateReportTemplate;

// ---------------------------------------------------------------------------
// System-only: library toggle + tenant visibility (no tenant-web equivalent)
// ---------------------------------------------------------------------------

/**
 * @localFirst toggleReportTemplateLibrary
 * Source of Truth: local Dexie report_templates (system DB).
 * Write Flow: updateReportTemplate sets isInLibrary, then background sync.
 * Sync Strategy: replication to MongoDB. Conflict Resolution: last-write-wins via version bump.
 */
export function toggleReportTemplateLibrary(
  id: string,
  isInLibrary: boolean,
): Promise<ReportTemplateLocal | null> {
  return service.updateReportTemplate(id, { isInLibrary });
}

/**
 * @localFirst setReportTemplateVisibility
 * Source of Truth: local Dexie report_templates (system DB).
 * Write Flow: updateReportTemplate sets visibility (+ allowedTenantIds), then background sync.
 * Sync Strategy: replication to MongoDB. Conflict Resolution: last-write-wins via version bump.
 */
export function setReportTemplateVisibility(
  id: string,
  visibility: 'all-departments' | 'specific-tenants' | 'system-only',
  allowedTenantIds?: string[],
): Promise<ReportTemplateLocal | null> {
  const patch: Partial<ReportTemplateLocal> = { visibility };
  patch.allowedTenantIds = visibility === 'specific-tenants' && allowedTenantIds ? allowedTenantIds : undefined;
  return service.updateReportTemplate(id, patch);
}
